<template>
  <div
    ref="wrapperRef"
    class="user-select-wrapper"
    :class="{ 'is-open': isOpen, 'is-disabled': disabled }"
  >
    <div
      ref="triggerRef"
      class="user-select-trigger"
      :class="{ 'is-disabled': disabled }"
      @click="toggleDropdown"
    >
      <span v-if="selectedUser" class="user-select-value">
        <div style="display: flex; align-items: center; gap: 6px">
          <span class="user-username">{{
            selectedUser.username ? '@' + selectedUser.username : ''
          }}</span>
          <span v-if="selectedUser.role && selectedUser.role !== 'member'" class="user-role"
            >({{ selectedUser.role }})</span
          >
        </div>
        <span v-if="selectedUser.name" class="user-name">{{ selectedUser.name }}</span>
        <span v-if="!selectedUser.username && !selectedUser.name" class="user-fallback"
          >User {{ selectedUser.id }}</span
        >
      </span>
      <span v-else class="user-select-placeholder">{{ placeholder }}</span>
      <span class="user-select-arrow">▼</span>
    </div>
    <Teleport to="body">
      <div v-if="isOpen" ref="dropdownRef" class="user-select-dropdown" :style="dropdownStyle">
        <div v-if="effectiveSearchable" class="user-select-search">
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            :placeholder="actualSearchPlaceholder"
            class="user-select-search-input"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            @input="handleSearchInput"
            @keyup="handleSearchInput"
            @keydown="handleSearchInput"
            @change="handleSearchInput"
            @compositionupdate="handleSearchInput"
            @paste="handleSearchInput"
          />
        </div>
        <div v-if="loading && !effectiveLocalSearch" class="user-select-loading">
          {{ __('Loading…') }}
        </div>
        <div
          v-else-if="displayUsers.length === 0 && (searchQuery || !effectiveLocalSearch)"
          class="user-select-no-results"
        >
          {{
            searchQuery ? __('No users found') : __('Type at least 2 characters to find the User.')
          }}
        </div>
        <div v-else>
          <div
            v-if="showEmptyOption"
            class="user-select-option"
            :class="{ 'is-selected': !selectedValue }"
            @click="selectUser(null)"
          >
            <span class="user-select-placeholder-text">{{ placeholder }}</span>
          </div>
          <div
            v-for="user in displayUsers"
            :key="user.id"
            class="user-select-option"
            :class="{ 'is-selected': String(selectedValue) === String(user.id) }"
            @click="selectUser(user)"
          >
            <div class="user-option-content">
              <div style="display: flex; align-items: center; gap: 6px">
                <div class="user-username">{{ user.username ? '@' + user.username : '' }}</div>
                <span v-if="user.role && user.role !== 'member'" class="user-role"
                  >({{ user.role }})</span
                >
              </div>
              <div v-if="user.name" class="user-name">{{ user.name }}</div>
              <div v-if="!user.username && !user.name" class="user-fallback">
                User {{ user.id }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useLazyQuery } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { __ } from '@/locale'
import { useUserCache, MAX_CACHEABLE_USERS } from '@/composables/useUserCache'

const props = defineProps({
  modelValue: { type: [String, Number], default: null },
  users: { type: Array, required: false, default: () => [] },
  placeholder: { type: String, default: 'Select user' },
  showEmptyOption: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
  searchable: { type: Boolean, default: false },
  searchPlaceholder: { type: String, default: null },
  localSearch: { type: Boolean, default: true }, // When true, filters props.users locally instead of backend search
  projectId: { type: [String, Number], default: null }, // Project ID for caching
})

const { getCachedUsers, setCachedUsers, shouldUseCache } = useUserCache()

// Auto-enable searchable if projectId is provided or if users count is high
const effectiveSearchable = computed(() => {
  if (props.searchable) return true
  if (props.projectId) return true
  if (props.users && props.users.length >= 50) return true // Auto-enable for large lists
  return false
})

// Determine if we should use local search (cached or provided users)
const effectiveLocalSearch = computed(() => {
  if (!effectiveSearchable.value) return true
  if (props.localSearch === false) return false // Explicitly disabled

  // If projectId is provided, check if we should use cache
  if (props.projectId) {
    // Use local search if we have cached users or provided users are cacheable (< 200)
    if (cachedUsers.value) return true
    if (props.users && shouldUseCache(props.users)) return true
    // If users.length >= 200, use backend search
    if (props.users && props.users.length >= MAX_CACHEABLE_USERS) return false
  }

  // Default to local search if explicitly set or if we have users to search locally
  return props.localSearch !== false && props.users && props.users.length > 0
})

// Load cached users on mount if projectId is provided
const cachedUsers = ref(null)
const allAvailableUsers = computed(() => {
  // Prioritize props.users if provided and non-empty, otherwise use cached users
  if (props.users && props.users.length > 0) {
    return props.users
  }
  return cachedUsers.value || props.users
})

// Load cache on mount
onMounted(() => {
  if (props.projectId) {
    const cached = getCachedUsers(props.projectId)
    if (cached) {
      cachedUsers.value = cached
    }
  }

  // Cache users if they're provided and cacheable
  if (props.users && props.users.length > 0 && props.projectId && shouldUseCache(props.users)) {
    setCachedUsers(props.projectId, props.users)
  }
})

// Watch for users prop changes and cache them
watch(
  () => props.users,
  newUsers => {
    if (newUsers && newUsers.length > 0 && props.projectId && shouldUseCache(newUsers)) {
      setCachedUsers(props.projectId, newUsers)
    }
  },
  { immediate: true }
)

const emit = defineEmits(['update:modelValue'])

const isOpen = ref(false)
const selectedValue = computed(() => props.modelValue)
const actualSearchPlaceholder = computed(
  () => props.searchPlaceholder || __('Search by name or username...')
)
const wrapperRef = ref(null)
const triggerRef = ref(null)
const dropdownRef = ref(null)
const dropdownStyle = ref({})
const searchInputRef = ref(null)
const searchQuery = ref('')
const debounceTimer = ref(null)

const SEARCH_USERS_QUERY = gql`
  query SearchUsers($query: String!, $limit: Int) {
    searchUsers(query: $query, limit: $limit) {
      id
      username
      name
      telegramUserId
    }
  }
`

const {
  load: searchUsers,
  result: searchResult,
  loading,
} = useLazyQuery(SEARCH_USERS_QUERY, () => ({
  query: searchQuery.value.trim(),
  limit: 20,
}))

const searchedUsers = ref([])

watch(searchResult, data => {
  if (data?.searchUsers) {
    searchedUsers.value = data.searchUsers || []
  }
})

const displayUsers = computed(() => {
  if (effectiveSearchable.value && searchQuery.value && searchQuery.value.trim()) {
    if (effectiveLocalSearch.value) {
      // Filter users locally by username or name
      const query = searchQuery.value.toLowerCase().trim()
      if (!query) {
        return allAvailableUsers.value
      }
      return allAvailableUsers.value.filter(user => {
        const username = (user.username || '').toLowerCase()
        const name = (user.name || '').toLowerCase()
        return username.includes(query) || name.includes(query)
      })
    }
    // Use backend search results
    return searchedUsers.value
  }
  return allAvailableUsers.value
})

const selectedUser = computed(() => {
  if (
    selectedValue.value === null ||
    selectedValue.value === undefined ||
    selectedValue.value === ''
  )
    return null
  const allUsers = effectiveSearchable.value
    ? [...allAvailableUsers.value, ...searchedUsers.value]
    : allAvailableUsers.value
  return allUsers.find(u => String(u.id) === String(selectedValue.value)) || null
})

function handleSearchInput(event) {
  if (!effectiveSearchable.value) return

  // Update searchQuery from event target if v-model didn't update (mobile fix)
  if (event && event.target) {
    const newValue = event.target.value
    if (searchQuery.value !== newValue) {
      searchQuery.value = newValue
    }
  }

  // Local search doesn't need backend query - filtering happens in displayUsers computed
  if (effectiveLocalSearch.value) return

  // Clear existing timer
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value)
  }

  // Debounce search query for backend search
  debounceTimer.value = setTimeout(() => {
    const query = searchQuery.value.trim()
    if (query.length >= 2) {
      searchUsers()
    } else {
      searchedUsers.value = []
    }
  }, 300)
}

function updateDropdownPosition() {
  if (!isOpen.value || !triggerRef.value) return

  nextTick(() => {
    if (!triggerRef.value) return

    const rect = triggerRef.value.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const dropdownMaxHeight = 200 // max-height from CSS
    const spaceBelow = viewportHeight - rect.bottom
    const spaceAbove = rect.top

    // For position: fixed, use viewport coordinates directly (no scroll offset needed)
    // Position dropdown above trigger if there's not enough space below
    const shouldPositionAbove = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow

    dropdownStyle.value = {
      position: 'fixed',
      top: shouldPositionAbove ? `${rect.top - dropdownMaxHeight}px` : `${rect.bottom}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      zIndex: 10000,
      maxHeight: shouldPositionAbove
        ? `${Math.min(dropdownMaxHeight, spaceAbove - 10)}px`
        : `${Math.min(dropdownMaxHeight, spaceBelow - 10)}px`,
    }
  })
}

function toggleDropdown() {
  if (props.disabled) return
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    updateDropdownPosition()
    // Focus search input if searchable, but prevent scroll to avoid page bounce
    if (effectiveSearchable.value) {
      nextTick(() => {
        if (searchInputRef.value) {
          // Save current scroll position
          const scrollY = window.scrollY
          const scrollX = window.scrollX

          // Focus with preventScroll option (modern browsers)
          searchInputRef.value.focus({ preventScroll: true })

          // Restore scroll position if browser doesn't support preventScroll
          requestAnimationFrame(() => {
            if (window.scrollY !== scrollY || window.scrollX !== scrollX) {
              window.scrollTo(scrollX, scrollY)
            }
          })
        }
      })
    }
  } else {
    // Clear search when closing
    if (effectiveSearchable.value) {
      searchQuery.value = ''
      searchedUsers.value = []
    }
  }
}

function selectUser(user) {
  emit('update:modelValue', user ? user.id : null)
  isOpen.value = false
}

function handleClickOutside(event) {
  if (!isOpen.value) return
  if (wrapperRef.value && wrapperRef.value.contains(event.target)) return
  if (dropdownRef.value && dropdownRef.value.contains(event.target)) return
  isOpen.value = false
}

function handleScroll() {
  if (isOpen.value) {
    updateDropdownPosition()
  }
}

function handleResize() {
  if (isOpen.value) {
    updateDropdownPosition()
  }
}

watch(isOpen, newValue => {
  if (newValue) {
    updateDropdownPosition()
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)
  } else {
    window.removeEventListener('scroll', handleScroll, true)
    window.removeEventListener('resize', handleResize)
  }
})

// Watch searchQuery to ensure reactivity on mobile devices
watch(searchQuery, () => {
  // Trigger handleSearchInput to ensure backend search works
  if (!effectiveLocalSearch.value) {
    handleSearchInput()
  }
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('scroll', handleScroll, true)
  window.removeEventListener('resize', handleResize)
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value)
  }
})
</script>

<style scoped>
.user-select-wrapper {
  position: relative;
  width: 100%;
}

.user-select-trigger {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 38px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.user-select-trigger:hover:not(.is-disabled) {
  border-color: #999;
}

.user-select-trigger.is-disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  opacity: 0.6;
}

.user-select-wrapper.is-open .user-select-trigger {
  border-color: #1d6bf3;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.user-select-value {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.user-select-placeholder {
  color: #999;
  flex: 1;
}

.user-select-placeholder-text {
  color: #999;
}

.user-select-arrow {
  font-size: 10px;
  color: #666;
  margin-left: 8px;
  transition: transform 0.2s;
}

.user-select-wrapper.is-open .user-select-arrow {
  transform: rotate(180deg);
}

.user-select-dropdown {
  background: white;
  border: 1px solid #1d6bf3;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.user-select-search {
  padding: 8px;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.user-select-search-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
}

.user-select-search-input:focus {
  border-color: #1d6bf3;
}

.user-select-loading,
.user-select-no-results {
  padding: 12px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.user-select-option {
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.user-select-option:last-child {
  border-bottom: none;
}

.user-select-option:hover {
  background-color: #f8f9fa;
}

.user-select-option.is-selected {
  background-color: #e3f2fd;
}

.user-option-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-username {
  font-weight: 500;
  color: #212529;
  font-size: 14px;
}

.user-name {
  font-size: 12px;
  color: #9e9e9e;
  font-weight: 400;
}

.user-fallback {
  font-size: 14px;
  color: #757575;
  font-style: italic;
}

.user-role {
  font-size: 12px;
  color: #666;
  font-weight: 400;
}

@media (max-width: 767px) {
  .user-select-search-input {
    font-size: 16px;
  }
}
</style>
